import {
  MARKET_SECTION_DESCRIPTIONS,
  historyDescription,
} from './sectionDescriptions'
import type { AiHandoffSectionId } from '@popety-io/types'
import {
  SECTION_SPECS,
  calc,
  geo,
  round0,
  round1,
  type SectionField,
  type SectionSpec,
} from './sectionFields'

/**
 * Land, building and estimate shares are written by the SAME fetchers, so the
 * sections below are byte-identical across all three — verified against
 * production for noise (`road_day`/`railway_night` …), sunlight (`annual`),
 * transport (`publicTransportMetrics`), amenities (`category_scores`),
 * demographics, employment, taxes and electricity.
 *
 * Reusing the estimate's field map is therefore correct AND the only way the
 * three artifacts cannot drift apart. What is NOT reused is anything whose
 * shape differs: the markets nest one level deeper here and carry absolute
 * CHF where the estimate carries CHF/m2, so they are declared from scratch
 * below. Cloning those would have mislabelled every figure.
 */
const cloneSpec = (
  fromId: AiHandoffSectionId,
  toId: AiHandoffSectionId
): SectionSpec => {
  const base = SECTION_SPECS.find((s) => s.id === fromId)
  if (!base) {
    throw new Error(`No estimate section spec to reuse for "${fromId}"`)
  }
  return { ...base, id: toId, source_key: toId as string }
}

/** The eight sections whose stored shape is identical across artifacts. */
export const sharedAnalysisSpecs = (): SectionSpec[] => [
  cloneSpec('noise_analysis', 'noise'),
  cloneSpec('sunlight', 'sunlight'),
  cloneSpec('transport_access', 'transport'),
  cloneSpec('amenities', 'amenities'),
  cloneSpec('demographics', 'demographics'),
  cloneSpec('employment', 'employment'),
  cloneSpec('taxes', 'taxes'),
  cloneSpec('electricity', 'electricity'),
]

/**
 * One quarter of the price-over-time chart. The stored buckets are an object
 * keyed by quarter ("2006-Q1"), which `seriesRows` turns into rows carrying
 * that key.
 *
 * The unit here is CHF, NOT CHF/m2. Land and building shares store
 * `priceEvolution` (absolute asking prices, ~2.4M in production), where the
 * estimate stores `priceEvolutionPerSqm`. Same path, different quantity.
 */
/**
 * A price. Non-positive values become an explicit `null`: 27 of 241 saved
 * listings carry `price: 0`, which means "price on request", not free, and a
 * land-registry 0 covers both gratuitous transfers and undisclosed sale
 * prices. Emitting 0 CHF would assert a property is free; emitting null says
 * the price is unknown, which is what the source actually means.
 */
const positive = (v: number): number | null => (v > 0 ? round0(v) : null)

/**
 * Same for measurements. Production listings carry `area: -1`, `rooms_nb: -1`
 * and `bathroom_nb: -1` as "unknown" sentinels; -1 m2 is not a floor area, so
 * the figure is emitted with a null value rather than the sentinel.
 */
const positive1 = (v: number): number | null => (v > 0 ? round1(v) : null)

const chf = (
  path: string,
  key: string,
  unit: 'CHF' | 'CHF/month'
): SectionField => ({
  path,
  key,
  unit,
  source: 'spatial_analysis',
  transform: positive,
})

const priceOverTime = (
  key: string,
  unit: 'CHF' | 'CHF/month'
): SectionField => ({
  path: 'priceEvolution.price_over_time.buckets',
  key,
  source: 'spatial_analysis',
  kind: 'series',
  period_key: 'period',
  item: [
    calc('doc_count', 'sample_size', 'count'),
    chf('total_avg_price.value', 'average', unit),
    chf('overall_median.values.[50.0]', 'median', unit),
    chf('overall_quantiles.values.[10.0]', 'p10', unit),
    chf('overall_quantiles.values.[90.0]', 'p90', unit),
  ],
})

/**
 * Price by property category AND room count, flattened from a two-level
 * aggregation so each row is directly chartable.
 */
const priceByRooms = (
  path: string,
  key: string,
  unit: 'CHF' | 'CHF/month' | 'CHF/m2' | 'CHF/m2/month'
): SectionField => ({
  path,
  key,
  source: 'spatial_analysis',
  kind: 'series',
  period_key: 'property_category',
  explode: 'by_rooms.buckets',
  explode_key: 'rooms_nb',
  explode_key_min: 0.5,
  item: [
    calc('doc_count', 'sample_size', 'count'),
    {
      path: 'avg_price.value',
      key: 'average',
      unit,
      source: 'spatial_analysis',
      transform: round0,
    },
    {
      path: 'median_price.values.[50.0]',
      key: 'median',
      unit,
      source: 'spatial_analysis',
      transform: round0,
    },
  ],
})

/**
 * A market section on a land or building share. Both are stored as
 * `<section>.<root>` — `purchase_market.purchase` and `rental_market.rent` —
 * which `source_key` resolves as a dot path.
 */
export const marketSpec = (
  id: AiHandoffSectionId,
  root: 'purchase' | 'rent',
  title: string
): SectionSpec => {
  // Rents are stored as MONTHLY figures — production quarters average ~1'850
  // and ~3'070 CHF, which are monthly rents, not sale prices. Labelling them
  // plain CHF would leave the reader to guess the period.
  const unit = root === 'rent' ? 'CHF/month' : 'CHF'
  const perM2 = root === 'rent' ? 'CHF/m2/month' : 'CHF/m2'
  return {
    id,
    source_key: `${id as string}.${root}`,
    title,
    description: MARKET_SECTION_DESCRIPTIONS[root],
    fields: [
      priceOverTime('price_over_time', unit),
      priceByRooms('pricePerRoom.by_category.buckets', 'price_by_rooms', unit),
      priceByRooms(
        'priceM2PerRoom.by_category.buckets',
        'price_per_m2_by_rooms',
        perM2
      ),
      // New-build vs existing stock. Present on purchase markets only; the
      // fields simply do not resolve on a rental market and are dropped.
      {
        path: 'newVsResale.avg_price_per_sqm_new',
        key: 'new_build_average_chf_per_m2',
        unit: 'CHF/m2',
        source: 'spatial_analysis',
        transform: round0,
      },
      {
        path: 'newVsResale.avg_price_per_sqm_existing',
        key: 'existing_average_chf_per_m2',
        unit: 'CHF/m2',
        source: 'spatial_analysis',
        transform: round0,
      },
      calc('newVsResale.total_new', 'new_build_sample_size', 'count'),
      calc('newVsResale.total_existing', 'existing_sample_size', 'count'),
    ],
  }
}

/**
 * Listings carried in a share's history.
 *
 * `description` is deliberately absent: it is free text written by the listing
 * agent and routinely carries broker names and phone numbers. `urls_image` IS
 * included — image links were explicitly asked for and contain no personal
 * data.
 */
export const listingRows = (path: string, key: string): SectionField => ({
  path,
  key,
  source: 'spatial_analysis',
  kind: 'series',
  item: [
    { path: 'listing_timestamp', key: 'listed_on', source: 'spatial_analysis' },
    { path: 'deal_type', key: 'deal_type', source: 'spatial_analysis' },
    {
      path: 'property_category',
      key: 'property_category',
      source: 'spatial_analysis',
    },
    { path: 'property_type', key: 'property_type', source: 'spatial_analysis' },
    // Guarded, not `money()`: history listings carry `price: 0` for
    // price-on-request the same way saved listings do. The unit comes from the
    // row's own deal_type — a history array mixes sales and lets, so a static
    // CHF would label monthly rents as sale prices.
    {
      path: 'price',
      key: 'price',
      unit: 'CHF',
      unit_by: {
        path: 'deal_type',
        map: { purchase: 'CHF', rent: 'CHF/month' },
      },
      source: 'spatial_analysis',
      transform: positive,
    },
    {
      path: 'price_per_square_meter',
      key: 'price_per_m2',
      unit: 'CHF/m2',
      unit_by: {
        path: 'deal_type',
        map: { purchase: 'CHF/m2', rent: 'CHF/m2/month' },
      },
      source: 'spatial_analysis',
      transform: positive,
    },
    {
      path: 'rooms_nb',
      key: 'rooms_nb',
      unit: 'count',
      source: 'spatial_analysis',
      transform: positive1,
    },
    {
      path: 'land_area',
      key: 'land_area_m2',
      unit: 'm2',
      source: 'spatial_analysis',
      transform: positive1,
    },
    {
      path: 'listing_duration',
      key: 'days_on_market',
      unit: 'count',
      source: 'spatial_analysis',
      transform: round0,
    },
    { path: 'active', key: 'is_active', source: 'spatial_analysis' },
    {
      path: 'urls_image',
      key: 'image_urls',
      source: 'spatial_analysis',
      kind: 'list',
    },
  ],
})

/**
 * Land-registry transactions.
 *
 * Only aggregate and monetary fields cross. `transaction_raw_data`,
 * `transaction_participants` and `transaction_step_text` are excluded by
 * policy: verified in production, they carry former owners' names, dates of
 * birth and free-text notarial wording.
 */
export const transactionRows = (path: string, key: string): SectionField => ({
  path,
  key,
  source: 'registre_foncier',
  kind: 'series',
  item: [
    {
      path: 'transaction_date',
      key: 'transaction_date',
      source: 'registre_foncier',
    },
    {
      path: 'transaction_step_name',
      key: 'transaction_type',
      source: 'registre_foncier',
    },
    {
      path: 'transaction_total_price',
      key: 'total_price_chf',
      unit: 'CHF',
      source: 'registre_foncier',
      // A stored 0 means either a gratuitous transfer (heritage, donation,
      // partage) or an undisclosed price on a real sale — verified in
      // production, both occur. Emitting 0 CHF would assert the property
      // changed hands for nothing; `transaction_type` already tells the
      // reader when it was an inheritance, so the figure is simply omitted.
      transform: positive,
    },
    {
      path: 'buyers_count',
      key: 'buyers_count',
      unit: 'count',
      source: 'registre_foncier',
    },
    {
      path: 'sellers_count',
      key: 'sellers_count',
      unit: 'count',
      source: 'registre_foncier',
    },
    {
      path: 'properties_count',
      key: 'properties_count',
      unit: 'count',
      source: 'registre_foncier',
    },
  ],
})

/**
 * Construction permits.
 *
 * `development_classification_llm` and its `_rationale` are excluded: machine
 * classifications are never published as fact. `participants` (identities) and
 * `description` (free text) are excluded for the same reason as above. The
 * RegBL flags that remain are federal register booleans.
 */
export const permitRows = (path: string, key: string): SectionField => ({
  path,
  key,
  source: 'geoportal',
  kind: 'series',
  item: [
    {
      path: 'inquiry_start_date',
      key: 'inquiry_start_date',
      source: 'geoportal',
    },
    {
      path: 'inquiry_approval_date',
      key: 'approval_date',
      source: 'geoportal',
    },
    {
      path: 'publication_type_name',
      key: 'publication_type',
      source: 'geoportal',
    },
    { path: 'work_type_official', key: 'work_type', source: 'geoportal' },
    {
      path: 'official_final_decision',
      key: 'final_decision',
      source: 'geoportal',
    },
    {
      path: 'land_use_principal_type',
      key: 'land_use_principal_type',
      source: 'geoportal',
    },
    {
      path: 'regbl_energetic_renovation',
      key: 'regbl_energetic_renovation',
      source: 'geoportal',
    },
    {
      path: 'regbl_heating_system_renovation',
      key: 'regbl_heating_system_renovation',
      source: 'geoportal',
    },
    {
      path: 'regbl_solar_photovoltaic_installation',
      key: 'regbl_solar_photovoltaic_installation',
      source: 'geoportal',
    },
    {
      path: 'regbl_interior_transformation',
      key: 'regbl_interior_transformation',
      source: 'geoportal',
    },
    {
      path: 'municipality_name',
      key: 'municipality_name',
      source: 'geoportal',
    },
  ],
})

/** History: listings, transactions and permits attached to the subject. */
export const historySpec = (opts: {
  transactions: boolean
  permits: boolean
}): SectionSpec => ({
  id: 'history',
  title: 'History',
  description: historyDescription(opts),
  fields: [
    listingRows('listings', 'listings'),
    ...(opts.transactions
      ? [transactionRows('transactions', 'transactions')]
      : []),
    ...(opts.permits
      ? [permitRows('buildingPermits', 'construction_permits')]
      : []),
  ],
})

export { cloneSpec, geo, round0, round1 }

/**
 * A listing the customer explicitly SAVED on the analysis, stored as the full
 * listing document (~52 fields) rather than the leaner shape carried in
 * `history`. These are the comparables a reader most wants, so the useful
 * numeric fields are emitted in full, plus the asking-price history.
 *
 * Excluded by policy: `broker_info` and `vendor_type` (agent identity),
 * `description` / `description_fr` / `title` / `title_fr` (free text that
 * routinely carries names and phone numbers), `exact_location` (finer than the
 * address the listing publishes), and `scraping_source` / `listing_portals`
 * (which portal a listing came from is not ours to republish).
 */
export const savedListingRows = (
  path: string,
  key: string,
  unit: 'CHF' | 'CHF/month',
  perM2: 'CHF/m2' | 'CHF/m2/month'
): SectionField => ({
  path,
  key,
  source: 'spatial_analysis',
  kind: 'series',
  item: [
    { path: 'listing_timestamp', key: 'listed_on', source: 'spatial_analysis' },
    { path: 'deal_type', key: 'deal_type', source: 'spatial_analysis' },
    {
      path: 'property_category',
      key: 'property_category',
      source: 'spatial_analysis',
    },
    { path: 'property_type', key: 'property_type', source: 'spatial_analysis' },
    { path: 'address', key: 'address', source: 'spatial_analysis' },
    {
      path: 'postal_code_name',
      key: 'locality',
      source: 'spatial_analysis',
    },
    chf('price', 'price', unit),
    {
      path: 'price_per_square_meter',
      key: 'price_per_m2',
      unit: perM2,
      source: 'spatial_analysis',
      transform: positive,
    },
    {
      path: 'area',
      key: 'living_area_m2',
      unit: 'm2',
      source: 'spatial_analysis',
      transform: positive1,
    },
    {
      path: 'land_area',
      key: 'land_area_m2',
      unit: 'm2',
      source: 'spatial_analysis',
      transform: positive1,
    },
    {
      path: 'rooms_nb',
      key: 'rooms_nb',
      unit: 'count',
      source: 'spatial_analysis',
      transform: positive1,
    },
    {
      path: 'bathroom_nb',
      key: 'bathroom_nb',
      unit: 'count',
      source: 'spatial_analysis',
      transform: positive1,
    },
    { path: 'floor', key: 'floor', unit: 'count', source: 'spatial_analysis' },
    {
      path: 'construction_year',
      key: 'construction_year',
      unit: 'year',
      source: 'spatial_analysis',
    },
    {
      path: 'new_construction',
      key: 'is_new_construction',
      source: 'spatial_analysis',
    },
    {
      path: 'listing_duration',
      key: 'days_on_market',
      unit: 'count',
      source: 'spatial_analysis',
      transform: round0,
    },
    { path: 'active', key: 'is_active', source: 'spatial_analysis' },
    { path: 'price_trend', key: 'price_trend', source: 'spatial_analysis' },
    {
      path: 'urls_image',
      key: 'image_urls',
      source: 'spatial_analysis',
      kind: 'list',
    },
    // Listing copy and provenance. Included at the product owner's direction:
    // this is text the listing itself published, and the portals it appeared
    // on. `exact_location` is a BOOLEAN flag meaning the published position is
    // exact — not a finer coordinate.
    { path: 'title', key: 'title', source: 'spatial_analysis' },
    { path: 'description', key: 'description', source: 'spatial_analysis' },
    {
      path: 'exact_location',
      key: 'has_exact_location',
      source: 'spatial_analysis',
    },
    { path: 'vendor_type', key: 'vendor_type', source: 'spatial_analysis' },
    {
      path: 'scraping_source',
      key: 'scraping_source',
      source: 'spatial_analysis',
      kind: 'list',
    },
    {
      path: 'listing_portals',
      key: 'listing_portal_names',
      source: 'spatial_analysis',
      kind: 'list',
      list_path: 'name',
    },
    {
      path: 'listing_portals',
      key: 'listing_portal_urls',
      source: 'spatial_analysis',
      kind: 'list',
      list_path: 'original_url',
    },
    // `broker_info` is an object; flattened rather than emitted whole so each
    // piece keeps a name of its own.
    {
      path: 'broker_info.mandate',
      key: 'broker_mandate',
      source: 'spatial_analysis',
    },
    {
      path: 'broker_info.type',
      key: 'broker_type',
      source: 'spatial_analysis',
    },
    {
      path: 'broker_info.contact_names',
      key: 'broker_contact_names',
      source: 'spatial_analysis',
      kind: 'list',
      list_path: 'name',
    },
    {
      path: 'broker_info.partner_names',
      key: 'broker_partner_names',
      source: 'spatial_analysis',
      kind: 'list',
    },
  ],
})

/**
 * The asking-price history of saved listings, flattened so each row is one
 * price on one date. Emitted as its own field because a nested array inside a
 * series row cannot be charted.
 */
export const savedListingPriceHistory = (
  path: string,
  key: string,
  unit: 'CHF' | 'CHF/month'
): SectionField => ({
  path,
  key,
  source: 'spatial_analysis',
  kind: 'series',
  period_key: 'address',
  period_path: 'address',
  explode: 'price_history',
  item: [
    {
      path: 'listing_timestamp',
      key: 'observed_on',
      source: 'spatial_analysis',
    },
    chf('price', 'price', unit),
  ],
})

/**
 * Construction permits the customer SAVED, stored as full permit documents.
 *
 * Excluded: `architect` and `owner` (party identities), `participants`,
 * `description` (free text), and every `development_classification_llm*` field
 * — machine classifications are never published as fact.
 */
export const savedPermitRows = (path: string, key: string): SectionField => ({
  path,
  key,
  source: 'geoportal',
  kind: 'series',
  item: [
    { path: 'permitNumber', key: 'permit_number', source: 'geoportal' },
    { path: 'parcelNumber', key: 'parcel_number', source: 'geoportal' },
    { path: 'filingDate', key: 'filing_date', source: 'geoportal' },
    { path: 'publicationDate', key: 'publication_date', source: 'geoportal' },
    { path: 'approvalDate', key: 'approval_date', source: 'geoportal' },
    {
      path: 'inquiry_start_date',
      key: 'inquiry_start_date',
      source: 'geoportal',
    },
    { path: 'status', key: 'status', source: 'geoportal' },
    {
      path: 'official_final_decision',
      key: 'final_decision',
      source: 'geoportal',
    },
    {
      path: 'publication_type_name',
      key: 'publication_type',
      source: 'geoportal',
    },
    { path: 'work_type_official', key: 'work_type', source: 'geoportal' },
    { path: 'category', key: 'category', source: 'geoportal' },
    { path: 'type', key: 'type', source: 'geoportal' },
    { path: 'authority', key: 'authority', source: 'geoportal' },
    { path: 'address', key: 'address', source: 'geoportal' },
    {
      path: 'municipality_name',
      key: 'municipality_name',
      source: 'geoportal',
    },
    {
      path: 'land_use_principal_type',
      key: 'land_use_principal_type',
      source: 'geoportal',
    },
    {
      path: 'regbl_energetic_renovation',
      key: 'regbl_energetic_renovation',
      source: 'geoportal',
    },
    {
      path: 'regbl_heating_system_renovation',
      key: 'regbl_heating_system_renovation',
      source: 'geoportal',
    },
    {
      path: 'regbl_solar_photovoltaic_installation',
      key: 'regbl_solar_photovoltaic_installation',
      source: 'geoportal',
    },
    {
      path: 'regbl_interior_transformation',
      key: 'regbl_interior_transformation',
      source: 'geoportal',
    },
    { path: 'url_web', key: 'url', source: 'geoportal' },
    // Permit parties. These are published in the cantonal official gazette
    // (FAO), so they are public record. Included at the product owner's
    // direction. Participant `address_*` is NOT included — 224 of 616
    // participants are private individuals with a home address attached, and
    // that was not part of the request.
    { path: 'architect', key: 'architect', source: 'geoportal' },
    { path: 'owner', key: 'owner', source: 'geoportal' },
    {
      path: 'participants',
      key: 'participant_names',
      source: 'geoportal',
      kind: 'list',
      list_path: 'full_name',
    },
  ],
})
